import { Button, Group, SegmentedControl, Stack } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconDownload } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useGenerators } from "../../../../hooks/useGenerators";
import { generatorSingleEndpointQueries } from "../../../../hooks/useGeneratorSingleEndpoint";
import { useGeneratorSingleEndpointList } from "../../../../hooks/useGeneratorSingleEndpointList";
import { usePageParams } from "../../../../hooks/usePageParams";
import { IconButton } from "../../../../mantine/components/IconButton/IconButton";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../../../mantine/getDefaultMRTRowEditModeOptions";
import type { Component } from "../../../../utils/backend-types";
import { UNNAMED_OBJECT_NAME } from "../../../../utils/constants";
import { DATE_DISPLAY_FORMAT } from "../../../../utils/dates/dates.constants";
import { ObjectName } from "../../../../utils/enums";
import { downloadAsExcel } from "../../../../utils/files/downloadAsExcel";
import { getLinkToComponentEditModal } from "../../../../utils/getLinkToComponentEditModal";
import { getIconColumn } from "../../../ComponentListContainer/ComponentList/columns/getIconColumn";
import { ComponentDeleteModal } from "../../../ComponentListContainer/ComponentList/ComponentDeleteModal/ComponentDeleteModal";
import { ComponentOpenEditModalLink } from "../../../ComponentListContainer/ComponentOpenEditModalLink/ComponentOpenEditModalLink";
import { GeneratorWizard } from "../../../GeneratorWizard/GeneratorWizard";
import { LoadOrError } from "../../../LoadOrError/LoadOrError";
import type { GeneratorListItemResponse } from "../../../ObjectModals/GeneratorModal/GeneratorModal.types";
import { getAllgemeineDatenColumns } from "./GeneratorTabTables/getAllgemeineDatenColumns";
import { getEegAnlageColumns } from "./GeneratorTabTables/getEegAnlageColumns";
import { getStromsteuerColumns } from "./GeneratorTabTables/getStromsteuerColumns";

function getColIds(
  cols: Array<{ id?: string; accessorKey?: string }>
): string[] {
  return cols.flatMap((col) => {
    const id = col.id ?? col.accessorKey;
    return id != null ? [id] : [];
  });
}

// siteId doesn't affect column IDs, so 0 is fine here
const TAB_COLUMN_IDS: Record<string, string[]> = {
  general: getColIds(getAllgemeineDatenColumns({ projectId: "", siteId: 0 })),
  "eeg-anlage": getColIds(getEegAnlageColumns({ projectId: "", siteId: 0 })),
  "stromsteuerliche-anlage": getColIds(getStromsteuerColumns())
};

export const GENERATOR_TAB_SEARCH_PARAM = "erzeuger-tab";

export const GENERATOR_TABLE_TAB_OPTIONS = [
  {
    label: "Allgemeine Daten",
    value: "general"
  },
  {
    label: "EEG-Anlage Daten",
    value: "eeg-anlage"
  },
  {
    label: "Stromsteuer",
    value: "stromsteuerliche-anlage"
  }
];

interface GeneratorTabProps {
  siteId: number;
  variantId: number;
  projectId: string;
  graphCoordinates: { x: number; y: number };
  userHasAccess: boolean;
  setShowNotAccessibleModal: (show: boolean) => void;
}

function GeneratorTab({
  siteId,
  variantId,
  projectId,
  graphCoordinates,
  userHasAccess,
  setShowNotAccessibleModal
}: GeneratorTabProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { useURLParam } = usePageParams();
  const [tableTab, setTableTab] = useURLParam(
    GENERATOR_TAB_SEARCH_PARAM,
    GENERATOR_TABLE_TAB_OPTIONS[0].value
  );

  const {
    data: generatorListItems,
    isLoading,
    error
  } = useGeneratorSingleEndpointList({
    siteId
  });
  const { data: generators } = useGenerators({ siteId });

  const [isOpen, { toggle }] = useDisclosure(false);
  const [componentToDelete, setComponentToDelete] =
    useState<GeneratorListItemResponse | null>(null);

  const [exportIsLoading, setExportIsLoading] = useState(false);

  const isRedispatchImportActive =
    generators?.some((g) => g.isRedispatchImportActiveForOperator) ?? false;

  function handleClickExport() {
    setExportIsLoading(true);
    const exportCols = mrtTable
      .getAllColumns()
      .filter(
        (col) =>
          !col.id.includes("mrt-") &&
          col.id !== "icon" &&
          col.id !== "isRedispatchImportActive"
      );
    const headers = exportCols.map((col) => col.columnDef.header);
    const dataRows = mrtTable.getRowModel().rows.map((row) =>
      exportCols.map((col) => {
        const value = row.getValue(col.id);
        if (value instanceof Date) {
          return DateTime.fromJSDate(value).toFormat(DATE_DISPLAY_FORMAT);
        }
        return value != null ? String(value) : "";
      })
    );
    downloadAsExcel([headers, ...dataRows], ObjectName.Generator);
    setExportIsLoading(false);
  }

  const columnVisibility = useMemo(() => {
    const allIds = Object.values(TAB_COLUMN_IDS).flat();
    const activeIds =
      TAB_COLUMN_IDS[tableTab ?? GENERATOR_TABLE_TAB_OPTIONS[0].value] ??
      TAB_COLUMN_IDS[GENERATOR_TABLE_TAB_OPTIONS[0].value];
    return {
      ...Object.fromEntries(allIds.map((id) => [id, false])),
      ...Object.fromEntries(activeIds.map((id) => [id, true])),
      isRedispatchImportActive:
        tableTab === "general" && isRedispatchImportActive
    };
  }, [tableTab, isRedispatchImportActive]);

  const columns = useMemo(() => {
    return [
      ...getIconColumn<GeneratorListItemResponse>(ObjectName.Generator, siteId),
      {
        header: "Name",
        accessorKey: "name",
        minSize: 200,
        Cell: ({ row }) => {
          const { id, name } = row.original;
          if (id == null) return <>{name ?? UNNAMED_OBJECT_NAME}</>;
          return (
            <ComponentOpenEditModalLink
              componentId={id}
              linkContent={name ?? UNNAMED_OBJECT_NAME}
              objectName={ObjectName.Generator}
              projectId={projectId}
              siteId={siteId}
            />
          );
        }
      },
      ...getAllgemeineDatenColumns({ projectId, siteId }),
      ...getEegAnlageColumns({ projectId, siteId }),
      ...getStromsteuerColumns()
    ];
  }, [projectId, siteId]);

  const mrtTable = useMantineReactTable<GeneratorListItemResponse>({
    ...getDefaultMRTOptions({
      emptyRowsFallbackText: `Keine ${ObjectName.Generator} vorhanden`,
      hideBorder: true
    }),
    ...getDefaultMRTRowEditModeOptions({
      rowActions: {
        edit: {
          text: `${ObjectName.Generator} bearbeiten`,
          onClick: (row) => {
            if (row.original.id === undefined) return;
            mrtTable.setEditingRow(null);
            navigate(
              getLinkToComponentEditModal(
                ObjectName.Generator,
                row.original.id,
                projectId,
                siteId
              ) + `/general?tab=${tableTab}`
            );
          }
        },
        delete: {
          text: `${ObjectName.Generator} löschen`,
          onClick: (row) => {
            if (!userHasAccess) {
              setShowNotAccessibleModal(true);
            } else {
              setComponentToDelete(row.original);
            }
          }
        }
      }
    }),
    renderTopToolbarCustomActions: () => (
      <Group align="center" justify="space-between" w="stretch">
        <SegmentedControl
          data={GENERATOR_TABLE_TAB_OPTIONS}
          value={tableTab}
          onChange={(value) => setTableTab(value)}
        />
        <IconButton
          color="primary"
          icon={IconDownload}
          loading={exportIsLoading}
          size="xs"
          onClick={handleClickExport}
        >
          Als Excel-Datei herunterladen
        </IconButton>
      </Group>
    ),
    state: {
      columnVisibility
    },
    layoutMode: "grid",
    enablePagination: false,
    enableColumnResizing: true,
    columns,
    data: generatorListItems ?? []
  });

  return (
    <>
      <LoadOrError error={error} loading={isLoading}>
        <Stack className="GeneratorTab" gap={0} pt="xs">
          <MantineReactTable table={mrtTable} />

          {componentToDelete && (
            <ComponentDeleteModal
              component={componentToDelete as unknown as Component}
              objectName={ObjectName.Generator}
              onDeleteSuccess={() => {
                queryClient.invalidateQueries(
                  generatorSingleEndpointQueries.list({ siteId })
                );
                setComponentToDelete(null);
              }}
              onToggle={() => setComponentToDelete(null)}
            />
          )}
        </Stack>
      </LoadOrError>
      <Group justify="center" mt="xl">
        <Button
          color="primary"
          data-disabled={!userHasAccess}
          onClick={() => {
            if (!userHasAccess) {
              setShowNotAccessibleModal(true);
            } else {
              toggle();
            }
          }}
        >
          {ObjectName.Generator} erstellen
        </Button>
      </Group>
      <GeneratorWizard
        graphCoordinates={graphCoordinates}
        isOpen={isOpen}
        siteId={siteId}
        variantId={variantId}
        onClose={toggle}
      />
    </>
  );
}

export { GeneratorTab, GeneratorTabProps };
